import {
    Alert,
    Asset,
    UserRecord,
    UserRole
} from '@tassat/fred-definitions';
import { AccountRecordExtended } from '@tassat/fred-extended-definitions';

import { ACGState } from 'Reducers/AccountClientGroups';
import { AccountPositionsState } from 'Reducers/accountPositions';
import { AccountRelationshipsState } from 'Reducers/accountRelationships';
import { AccountsState } from 'Reducers/accounts';
import { AccountsWithdrawalRelationshipsState } from 'Reducers/accountsWithdrawalRelationships';
import { AccountWithdrawalDataState } from 'Reducers/accountWithdrawalData';
import { ACGUserState } from 'Reducers/ACGUsers';
import { AddressBookState } from 'Reducers/addressbook';
import { APIConnectionFirmsState } from 'Reducers/apiconnectionfirms';
import { APIConnectionState } from 'Reducers/apiconnections';
import { APIConnectionUsersState } from 'Reducers/apiconnectionusers';
import { AssetBalanceStatsState } from 'Reducers/assetBalanceStats';
import { ClientMessageRecordMap } from 'Reducers/clientMessage';
import { DailyStatisticsState } from 'Reducers/dailyStatistics';
import { FirmApiTypeState } from 'Reducers/firmApiType';
import { FirmAssetBalanceStatisticsState } from 'Reducers/firmAssetBalanceStats';
import { FirmAssetEntitlementsState } from 'Reducers/firmAssetEntitlements';
import { FirmState } from 'Reducers/firms';
import { FirmStatsState } from 'Reducers/firmStats';
import { KillSwitchState } from 'Reducers/kllSwitch';
import { NetAssetValuesState } from 'Reducers/netAssetValues';
import { PCGUserState } from 'Reducers/PCGUsers';
import { PricesState } from 'Reducers/prices';
import { PCGState } from 'Reducers/PrivateClientGroups';
import { AdminState } from 'Reducers/store/adminStore';
import { ClientState } from 'Reducers/store/clientStore';
import { isClientState } from 'Reducers/store/hooks';
import { UserAccountsState } from 'Reducers/userAccounts';
import { UserApiTypeState } from 'Reducers/userApiTypes';
import { UserState } from 'Reducers/users';
import { ValidationState } from 'Reducers/validation';

export const getAccounts = (state: AdminState | ClientState): AccountsState => state.accounts || {};
export const getAccountPositions = (state: AdminState | ClientState): AccountPositionsState => state.accountPositions;
export const getAccountClientGroups = (state: AdminState | ClientState): ACGState => {
    return !isClientState(state) ? state.accountClientGroups : {} as ACGState;
};
export const getAccountRelationships = (state: AdminState | ClientState): AccountRelationshipsState => state.accountRelationships;
export const getAccountsWithdrawalRelationships = (state: AdminState | ClientState):
    AccountsWithdrawalRelationshipsState => state.accountsWithdrawalRelationships;
export const getAccountWithdrawalData = (state: AdminState | ClientState): AccountWithdrawalDataState => {
    return !isClientState(state) ? state.accountsWithdrawalData : {} as AccountWithdrawalDataState;
};
export const getAcgUsers = (state: AdminState | ClientState): ACGUserState => {
    return !isClientState(state) ? state.acgUsers : {} as ACGUserState;
};
export const getAddressBook = (state: AdminState | ClientState): AddressBookState => {
    return isClientState(state) ? state.addressBook : { contacts: {}};
};
export const getAlerts = (state: AdminState | ClientState): Alert[] => {
    return !isClientState(state) ? state.alerts : [];
};
export const getApiConnections = (state: AdminState | ClientState): APIConnectionState => state.apiConnections;
export const getApiConnectionFirms = (state: AdminState | ClientState): APIConnectionFirmsState => {
    return !isClientState(state) ? state.apiConnectionFirms : {};
};
export const getApiConnectionUsers = (state: AdminState | ClientState): APIConnectionUsersState => state.apiConnectionUsers;
export const getFirms = (state: AdminState | ClientState): FirmState => state.firms;
export const getFirmApiTypes = (state: AdminState | ClientState): FirmApiTypeState => {
    return !isClientState(state) ? state.firmApiTypes : {};
};
export const getFirmStats = (state: AdminState | ClientState): FirmStatsState => {
    return !isClientState(state) ? state.firmStats : {} as FirmStatsState;
};
export const getFirmAssetStats = (state: AdminState | ClientState): FirmAssetBalanceStatisticsState => {
    return !isClientState(state) ? state.firmAssetBalanceStats : {} as FirmAssetBalanceStatisticsState;
};
export const getFirmAssetEntitlements = (state: AdminState | ClientState): FirmAssetEntitlementsState => state.firmAssetEntitlements;
export const getMessageAdminValidation = (state: AdminState | ClientState): ClientMessageRecordMap => state.clientMessage.admin.validation;
export const getPcgUsers = (state: AdminState | ClientState): PCGUserState => {
    return !isClientState(state) ? state.pcgUsers : {} as PCGUserState;
};
export const getPrivateClientGroups = (state: AdminState | ClientState): PCGState => {
    return !isClientState(state) ? state.privateClientGroups : {} as PCGState;
};
export const getSelectedClientAccount = (state: AdminState | ClientState):
    AccountRecordExtended => state.accounts[state.ui.selectedAccountId];
export const getTransactionList = (state: AdminState | ClientState) => state.transactions.transactions;
export const getPledgeList = (state: AdminState | ClientState) => state.pledges.pledges;
export const getPledgeDetail = (state: AdminState | ClientState) => state.pledges.pledgeDetail;
export const getHaltRealtimePledges = (state: AdminState | ClientState): boolean => state.pledges.haltRealtimePledges;
export const getPendingTransactionList = (state: AdminState | ClientState) => state.transactions.pendingTransactions;
export const getSelectedAccountId = (state: AdminState | ClientState): string => state.ui.selectedAccountId;
export const getStatistics = (state: AdminState | ClientState): DailyStatisticsState => {
    return !isClientState(state) ? state.statistics : {} as DailyStatisticsState;
};
export const getUser = (state: AdminState | ClientState): UserRecord => state.user;
export const getUserAccounts = (state: AdminState | ClientState): UserAccountsState => state.userAccounts;
export const getUserAdminRoleId = (state: AdminState | ClientState): string => state.user?.AdminRoleId ?? '';
export const getUserId = (state: AdminState | ClientState): string => state.user?.UserId ?? '';
export const getUserRoles = (state: AdminState | ClientState): UserRole[] => {
    return !isClientState(state) ? state.ui.userRoles : [];
};
export const getUsers = (state: AdminState | ClientState): UserState => state.users;
export const getUserApiTypes = (state: AdminState | ClientState): UserApiTypeState => state.userApiTypes;
export const getAssets = (state: AdminState | ClientState): {
    [key: number]: Asset;
} => state.assets;
export const getWithdrawalInstructions = (state: AdminState | ClientState): AccountWithdrawalDataState => state.accountsWithdrawalData;
export const getNetAssetValues = (state: AdminState | ClientState): NetAssetValuesState => state.netAssetValues;
export const getAssetBalanceStats = (state: AdminState | ClientState): AssetBalanceStatsState => {
    return !isClientState(state) ? state.assetBalanceStats : {} as AssetBalanceStatsState;
};
export const getApiBalanceStats = (state: AdminState | ClientState): AssetBalanceStatsState => {
    return !isClientState(state) ? state.apiBalanceStats : {} as AssetBalanceStatsState;
};
export const getPrices = (state: AdminState | ClientState): PricesState => state.prices;
export const getFees = (state: AdminState | ClientState) => {
    return !isClientState(state) ? state.fees : {};
};
export const getLiquidityFacilityStats = (state: AdminState | ClientState) => {
    return !isClientState(state) ? state.liquidityFacilityStats : {};
};
export const getClientLiquidityFacilityStatus = (state: ClientState | AdminState) => state.ui.isLiquidityFacilityAvailable;
export const getKillSwitch = (state: AdminState | ClientState): KillSwitchState => state.killSwitch;
export const getCurrentRole = (state: AdminState | ClientState): UserRole => state.ui.currentRole;
export const getFetchedUserRoles = (state: AdminState | ClientState): boolean => state.ui.fetchedUserRoles;
export const getUiConnected = (state: AdminState | ClientState): boolean => state.ui.connected;
export const getUiDisconnected = (state: AdminState | ClientState): boolean => state.ui.disconnected;
export const getUiInactive = (state: AdminState | ClientState): boolean => state.ui.inactive;
export const getThemeRefreshed = (state: AdminState | ClientState): boolean => state.ui.themeRefreshed;
export const getValidation = (state: AdminState | ClientState): ValidationState => state.validation;